import React from 'react';
import { Case, CaseStatus } from '../../types';

/**
 * Props for the CaseCard component
 */
export interface CaseCardProps {
  case: Case;
  clientName: string;
  onClick: () => void;
}

/**
 * Status badge colors for case statuses
 */
const STATUS_COLORS: Record<CaseStatus, string> = {
  active: '#4caf50',
  closed: '#9e9e9e',
  'on-hold': '#ff9800',
};

/**
 * Pure function to get the full student name from a case
 * @param caseData - The case object
 * @returns The formatted student name (FirstName LastName)
 */
export function getStudentName(caseData: Case): string {
  return `${caseData.childFirstName} ${caseData.childLastName}`;
}

/**
 * Pure function to get the status badge color
 * @param status - The case status
 * @returns The color string for the status badge
 */
export function getStatusColor(status: CaseStatus): string {
  return STATUS_COLORS[status] || '#9e9e9e';
}

/**
 * Pure function to format the status text for display
 * @param status - The case status
 * @returns The formatted status text (capitalized, hyphen replaced with space)
 */
export function formatStatusText(status: CaseStatus): string {
  return status.replace('-', ' ');
}

/**
 * Pure function to get the school display value
 * @param school - The school name or undefined
 * @returns The school name or '-' if not provided
 */
export function getSchoolDisplay(school: string | undefined): string {
  return school || '-';
}

/**
 * Pure function to get the district display value
 * @param district - The school district or undefined
 * @returns The district name or '-' if not provided
 */
export function getDistrictDisplay(district: string | undefined): string {
  return district || '-';
}

/**
 * Pure function to get the parent/guardian display value
 * @param clientName - The client name
 * @returns The client name or '-' if empty
 */
export function getParentDisplay(clientName: string): string {
  return clientName || '-';
}

/**
 * CaseCard component - displays a single case in a clickable card format.
 * 
 * Displays:
 * - Student name (childFirstName + childLastName)
 * - Case status badge (active, closed, on-hold)
 * - School name
 * - School district
 * - Parent/guardian name (from associated client)
 * 
 * Requirements: 2.3, 5.1, 5.2
 */
export const CaseCard: React.FC<CaseCardProps> = ({ case: caseData, clientName, onClick }) => {
  const studentName = getStudentName(caseData);
  const statusColor = getStatusColor(caseData.status);
  const statusText = formatStatusText(caseData.status);
  const schoolDisplay = getSchoolDisplay(caseData.school);
  const districtDisplay = getDistrictDisplay(caseData.schoolDistrict);
  const parentDisplay = getParentDisplay(clientName);

  return (
    <div
      onClick={onClick}
      data-testid={`case-card-${caseData.caseId}`}
      style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s, transform 0.2s',
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Header: Student name and status badge */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h3 
          style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}
          data-testid={`case-card-student-name-${caseData.caseId}`}
        >
          {studentName}
        </h3>
        <span
          data-testid={`case-card-status-${caseData.caseId}`}
          style={{
            backgroundColor: statusColor,
            color: 'white',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            textTransform: 'capitalize',
          }}
        >
          {statusText}
        </span>
      </div>

      {/* Details grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px', color: '#666' }}>
        <div>
          <span style={{ fontWeight: 500, color: '#333' }}>School: </span>
          <span data-testid={`case-card-school-${caseData.caseId}`}>{schoolDisplay}</span>
        </div>
        <div>
          <span style={{ fontWeight: 500, color: '#333' }}>District: </span>
          <span data-testid={`case-card-district-${caseData.caseId}`}>{districtDisplay}</span>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <span style={{ fontWeight: 500, color: '#333' }}>Parent/Guardian: </span>
          <span data-testid={`case-card-parent-${caseData.caseId}`}>{parentDisplay}</span>
        </div>
      </div>
    </div>
  );
};
